import { useState, useEffect } from 'react';
import AuthModal from '../../../components/feature/AuthModal';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleSignIn = () => {
    setMobileMenuOpen(false);
    setAuthMode('signin');
    setAuthModalOpen(true);
  };

  const handleGetStarted = () => {
    setMobileMenuOpen(false);
    setAuthMode('signup');
    setAuthModalOpen(true);
  };

  const handleAboutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    window.REACT_APP_NAVIGATE('/about');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleWizeAIClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
    window.REACT_APP_NAVIGATE('/wizeai');
    setTimeout(() => window.scrollTo(0, 0), 50);
  };

  const handleConsultingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    window.REACT_APP_NAVIGATE('/consulting');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleTrainingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    window.REACT_APP_NAVIGATE('/training');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const navLinks = [
    { label: 'Features', href: '#features', onClick: (e: React.MouseEvent) => { setMobileMenuOpen(false); } },
    { label: 'Analytics', href: '#analytics', onClick: (e: React.MouseEvent) => { setMobileMenuOpen(false); } },
    { label: 'Security', href: '#security', onClick: (e: React.MouseEvent) => { setMobileMenuOpen(false); } },
    { label: 'Training', href: '/training', onClick: handleTrainingClick },
    { label: 'Consulting', href: '/consulting', onClick: handleConsultingClick },
    { label: 'About Us', href: '/about', onClick: handleAboutClick },
    { label: 'WizeAI', href: '/wizeai', onClick: handleWizeAIClick },
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-gray-950/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <nav className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            {/* Logo */}
            <div className="flex items-center space-x-4 lg:space-x-12">
              <a href="/" onClick={handleLogoClick} className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg relative">
                  <i className="ri-shield-check-line text-lg sm:text-xl text-gray-900"></i>
                  <div className="absolute inset-0 bg-teal-400/20 rounded-lg blur-sm"></div>
                </div>
                <span className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                  Protect<span className="text-teal-400">Wize</span>
                </span>
              </a>

              {/* Desktop Nav */}
              <div className="hidden lg:flex items-center space-x-8">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={link.onClick}
                    className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Desktop Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <button
                onClick={handleSignIn}
                className="px-5 py-2.5 text-gray-300 hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Sign In
              </button>
              <button
                onClick={handleGetStarted}
                className="px-6 py-2.5 bg-teal-500 text-gray-900 rounded-lg hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap font-semibold"
              >
                Get Started
              </button>
            </div>

            {/* Mobile: Sign In + Hamburger */}
            <div className="flex lg:hidden items-center gap-2">
              <button
                onClick={handleSignIn}
                className="px-3 py-2 text-sm text-gray-300 hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Sign In
              </button>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-teal-400 transition-colors cursor-pointer rounded-lg hover:bg-gray-800/50"
                aria-label="Open menu"
              >
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Drawer */}
      <div className={`fixed top-0 right-0 h-full w-72 z-50 bg-gray-950 border-l border-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out lg:hidden ${
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg">
              <i className="ri-shield-check-line text-lg text-gray-900"></i>
            </div>
            <span className="text-lg font-bold text-white" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
              Protect<span className="text-teal-400">Wize</span>
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Nav Links */}
        <div className="flex flex-col px-4 py-6 gap-1">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={link.onClick}
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-teal-400 hover:bg-gray-800/60 rounded-lg transition-all cursor-pointer whitespace-nowrap text-base font-medium"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Auth Buttons */}
        <div className="absolute bottom-0 left-0 right-0 px-6 py-6 border-t border-gray-800 flex flex-col gap-3">
          <button
            onClick={handleSignIn}
            className="w-full py-3 text-gray-300 hover:text-teal-400 border border-gray-700 hover:border-teal-500/50 rounded-lg transition-all cursor-pointer whitespace-nowrap font-medium"
          >
            Sign In
          </button>
          <button
            onClick={handleGetStarted}
            className="w-full py-3 bg-teal-500 text-gray-900 rounded-lg hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap font-semibold"
          >
            Get Started
          </button>
        </div>
      </div>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authMode}
      />
    </>
  );
}
